import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Plus, Calendar, Clock, Video, Loader2, Pencil, Trash2, MoreVertical } from 'lucide-react';
import { trainingAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import TeamGate from '../components/TeamGate';
import { useTeam } from '../context/TeamContext';

const Training = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewSession, setShowNewSession] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [newSession, setNewSession] = useState({
    name: '',
    date: new Date().toISOString().split('T')[0],
    description: ''
  });

  const fetchSessions = async () => {
    if (!user?.team_id) {
      setLoading(false);
      return;
    }
    try {
      const response = await trainingAPI.listSessions(user.team_id);
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch training sessions:', error);
      toast({ title: 'Failed to load sessions', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [user?.team_id]);

  const handleCreateSession = async () => {
    if (!newSession.name.trim()) {
      toast({ title: t('training.sessionName') + ' is required', variant: 'destructive' });
      return;
    }

    if (!user?.team_id) {
      toast({ title: 'Please select a team first', variant: 'destructive' });
      return;
    }

    try {
      const response = await trainingAPI.createSession(user.team_id, newSession);
      setSessions([response.data, ...sessions]);
      setNewSession({ name: '', date: new Date().toISOString().split('T')[0], description: '' });
      setShowNewSession(false);
      toast({ title: t('training.createSession') + ' successful' });
    } catch (error) {
      console.error('Failed to create session:', error);
      toast({ title: 'Failed to create session', variant: 'destructive' });
    }
  };

  const handleUpdateSession = async () => {
    if (!editingSession) return;
    try {
      console.log('Original editingSession:', editingSession);

      // Ensure date is in YYYY-MM-DD format
      let dateValue = editingSession.date;
      if (editingSession.date instanceof Date) {
        dateValue = editingSession.date.toISOString().split('T')[0];
      } else if (typeof editingSession.date === 'string' && editingSession.date.includes('T')) {
        dateValue = editingSession.date.split('T')[0];
      }

      const updateData = {
        name: editingSession.name,
        date: dateValue,
        description: editingSession.description
      };
      console.log('Sending update data:', updateData);

      const response = await trainingAPI.updateSession(editingSession.id, updateData);
      setSessions(sessions.map(s => s.id === editingSession.id ? response.data : s));
      setEditingSession(null);
      toast({ title: 'Session updated successfully' });
    } catch (error) {
      console.error('Failed to update session:', error);
      console.error('Error response:', error.response?.data);
      toast({ title: 'Failed to update session', variant: 'destructive' });
    }
  };

  const handleDeleteSession = async (e, sessionId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this session? This will remove all clip associations, but the original videos will remain.')) {
      return;
    }

    try {
      await trainingAPI.deleteSession(sessionId);
      setSessions(sessions.filter(s => s.id !== sessionId));
      toast({ title: 'Session deleted successfully' });
    } catch (error) {
      console.error('Failed to delete session:', error);
      toast({ title: 'Failed to delete session', variant: 'destructive' });
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    return `${mins} min`;
  };

  const { hasPermission } = useTeam();
  const isCoachOrAdmin = hasPermission('manage_training_sessions');

  return (
    <TeamGate title="Training Sessions">
      <div className="px-4 lg:px-8 pb-8 pt-4 md:pt-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-xl md:text-3xl font-bold text-white mb-1 md:mb-2">{t('training.title')}</h1>
            <p className="text-xs md:text-base text-gray-400">{t('training.subtitle')}</p>
          </div>
          <Dialog open={showNewSession} onOpenChange={setShowNewSession}>
            <DialogTrigger asChild>
              <Button className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="w-4 h-4 mr-2" />
                {t('training.newSession')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1a1a1b] border-gray-800">
              <DialogHeader>
                <DialogTitle className="text-white">{t('training.createSession')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="sessionName" className="text-white">{t('training.sessionName')}</Label>
                  <Input
                    id="sessionName"
                    value={newSession.name}
                    onChange={(e) => setNewSession({ ...newSession, name: e.target.value })}
                    placeholder={t('training.sessionName')}
                    className="bg-[#0a0a0b] border-gray-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sessionDate" className="text-white">{t('training.sessionDate')}</Label>
                  <Input
                    id="sessionDate"
                    type="date"
                    value={newSession.date}
                    onChange={(e) => setNewSession({ ...newSession, date: e.target.value })}
                    className="bg-[#0a0a0b] border-gray-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description" className="text-white">{t('training.description')}</Label>
                  <Textarea
                    id="description"
                    value={newSession.description}
                    onChange={(e) => setNewSession({ ...newSession, description: e.target.value })}
                    placeholder={t('training.description')}
                    className="bg-[#0a0a0b] border-gray-700 text-white min-h-[80px]"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleCreateSession} className="flex-1 bg-blue-600 hover:bg-blue-700">
                    {t('training.createSession')}
                  </Button>
                  <Button variant="ghost" onClick={() => setShowNewSession(false)} className="text-white">
                    {t('videoDetail.cancel')}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Sessions Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {sessions.map((session) => (
              <Card
                key={session.id}
                onClick={() => navigate(`/training/${session.id}`)}
                className="bg-[#1a1a1b] border-gray-800 hover:border-gray-600 cursor-pointer transition-all group relative border-l-2 border-l-blue-500/60 md:border-l-0 md:border"
              >
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-start justify-between mb-3 md:mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-semibold text-base md:text-lg mb-1 md:mb-2 group-hover:text-blue-400 transition-colors pr-16 md:pr-8 truncate">
                        {session.name}
                      </h3>
                      {session.description && (
                        <p className="text-xs md:text-sm text-gray-400 mb-2 md:mb-3 line-clamp-2">{session.description}</p>
                      )}
                    </div>
                    {isCoachOrAdmin && (
                      <div className="flex gap-1 absolute top-3 right-3 md:top-4 md:right-4 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-gray-400 hover:text-white hover:bg-gray-800"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingSession(session);
                          }}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-500/10"
                          onClick={(e) => handleDeleteSession(e, session.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3 md:gap-4 text-xs md:text-sm text-gray-500 mb-3 md:mb-4">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5 md:w-4 md:h-4" />
                      {new Date(session.date).toLocaleDateString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <Video className="w-3.5 h-3.5 md:w-4 md:h-4" />
                      {session.clip_count} {t('training.clips')}
                    </span>
                    {session.duration > 0 && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5 md:w-4 md:h-4" />
                        {formatDuration(session.duration)}
                      </span>
                    )}
                  </div>

                  <Button className="hidden md:flex w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white">
                    {t('playbook.view')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Session Modal */}
        <Dialog open={!!editingSession} onOpenChange={(open) => !open && setEditingSession(null)}>
          <DialogContent className="bg-[#1a1a1b] border-gray-800">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Training Session</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="editSessionName" className="text-white">{t('training.sessionName')}</Label>
                <Input
                  id="editSessionName"
                  value={editingSession?.name || ''}
                  onChange={(e) => setEditingSession({ ...editingSession, name: e.target.value })}
                  placeholder={t('training.sessionName')}
                  className="bg-[#0a0a0b] border-gray-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editSessionDate" className="text-white">{t('training.sessionDate')}</Label>
                <Input
                  id="editSessionDate"
                  type="date"
                  value={editingSession?.date || ''}
                  onChange={(e) => setEditingSession({ ...editingSession, date: e.target.value })}
                  className="bg-[#0a0a0b] border-gray-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editDescription" className="text-white">{t('training.description')}</Label>
                <Textarea
                  id="editDescription"
                  value={editingSession?.description || ''}
                  onChange={(e) => setEditingSession({ ...editingSession, description: e.target.value })}
                  placeholder={t('training.description')}
                  className="bg-[#0a0a0b] border-gray-700 text-white min-h-[80px]"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleUpdateSession} className="flex-1 bg-blue-600 hover:bg-blue-700">
                  Save Changes
                </Button>
                <Button variant="ghost" onClick={() => setEditingSession(null)} className="text-white">
                  {t('videoDetail.cancel')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {!loading && sessions.length === 0 && (
          <div className="text-center py-16">
            <Video className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">{t('training.noSessions')}</p>
          </div>
        )}
      </div>
    </TeamGate>
  );
};

export default Training;
